import type { ApiError } from './types';
import { getVendorToken } from '@/shared/lib/vendor-token';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
const DEFAULT_TIMEOUT_MS = 30_000;

class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly error: ApiError,
  ) {
    super(error.message);
    this.name = 'ApiClientError';
  }
}

class ApiTimeoutError extends Error {
  constructor(public readonly endpoint: string) {
    super(`Request to ${endpoint} timed out`);
    this.name = 'ApiTimeoutError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let error: ApiError;
    try {
      error = (await response.json()) as ApiError;
    } catch {
      error = {
        code: 'UNKNOWN_ERROR',
        message: response.statusText || 'An unexpected error occurred',
      };
    }
    throw new ApiClientError(response.status, error);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  timeoutMs?: number;
};

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const {
    body,
    headers: customHeaders,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    signal: callerSignal,
    ...rest
  } = options;

  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => {
    timeoutController.abort();
  }, timeoutMs);

  // Compose caller signal (e.g. from TanStack Query) with our timeout signal
  const signal = callerSignal
    ? AbortSignal.any([timeoutController.signal, callerSignal])
    : timeoutController.signal;

  const headers = new Headers({ 'Content-Type': 'application/json' });
  // BE requires vendor_token header on every request
  const token = getVendorToken();
  if (token) {
    headers.set('vendor_token', token);
  }
  if (customHeaders) {
    const custom = new Headers(customHeaders);
    custom.forEach((value, key) => {
      headers.set(key, value);
    });
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${endpoint}`, {
      credentials: 'include',
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal,
      ...rest,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Distinguish timeout from caller-initiated cancellation
      if (timeoutController.signal.aborted) {
        throw new ApiTimeoutError(endpoint);
      }
      throw err;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  return handleResponse<T>(response);
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

export { ApiClientError, ApiTimeoutError };
